/**
 * Notification Service for Web Push Notifications
 * Handles permission requests, subscription management, and sending notifications
 */

class NotificationService {
  constructor() {
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    this.subscription = null;
    this.serviceWorkerRegistration = null;
    this.audioContext = null;
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  /**
   * Check if notifications are supported by the browser
   */
  isNotificationSupported() {
    return this.isSupported;
  }

  /**
   * Initialize the service worker and notification system
   */
  async initialize() {
    try {
      // Initialize audio for iOS
      await this.initializeAudio();
      
      if (!this.isSupported) {
        console.warn('Push notifications are not fully supported, using fallback methods');
        return true; // Still return true to enable fallback notifications
      }

      // Register service worker
      this.serviceWorkerRegistration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      console.log('Service Worker registered successfully:', this.serviceWorkerRegistration);

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      return true;
    } catch (error) {
      console.error('Service Worker registration failed, using fallback notifications:', error);
      return true; // Still allow fallback notifications
    }
  }

  /**
   * Request notification permission from user
   */
  async requestPermission() {
    if (!this.isSupported) {
      return 'not-supported';
    }

    // Check current permission status
    let permission = Notification.permission;

    if (permission === 'default') {
      // Request permission
      permission = await Notification.requestPermission();
    }

    console.log('Notification permission:', permission);
    return permission;
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus() {
    if (!this.isSupported) {
      return 'not-supported';
    }
    return Notification.permission;
  }

  /**
   * Subscribe to push notifications
   * Note: This is a client-side only implementation
   * For full push notifications, you'd need a backend with VAPID keys
   */
  async subscribe() {
    if (!this.serviceWorkerRegistration) {
      throw new Error('Service worker not registered');
    }

    try {
      // For now, we'll use local notifications
      // In a full implementation, you'd use pushManager.subscribe() with VAPID keys
      console.log('Notification subscription ready (local notifications)');
      return true;
    } catch (error) {
      console.error('Failed to subscribe to notifications:', error);
      return false;
    }
  }

  /**
   * Create audio notification sound
   */
  async initializeAudio() {
    if (this.audioContext) return;
    
    try {
      // Create audio context for sound notifications
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // iOS requires user interaction to unlock audio
      if (this.isIOS && this.audioContext.state === 'suspended') {
        // Add a one-time click listener to unlock audio
        const unlockAudio = () => {
          this.audioContext.resume().then(() => {
            console.log('Audio context unlocked for iOS');
          });
          document.removeEventListener('click', unlockAudio);
          document.removeEventListener('touchstart', unlockAudio);
        };
        
        document.addEventListener('click', unlockAudio);
        document.addEventListener('touchstart', unlockAudio);
      }
    } catch (error) {
      console.warn('Audio context not supported:', error);
    }
  }

  /**
   * Play notification sound
   */
  async playNotificationSound(isEmergency = false) {
    if (!this.audioContext) {
      await this.initializeAudio();
    }
    
    if (!this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      // Different frequencies for different notification types
      oscillator.frequency.setValueAtTime(
        isEmergency ? 800 : 600, 
        this.audioContext.currentTime
      );
      
      oscillator.type = 'sine';
      
      // Volume envelope
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, this.audioContext.currentTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.5);
      
      // For emergency, play multiple beeps
      if (isEmergency) {
        setTimeout(() => {
          this.playNotificationSound(false);
        }, 600);
        setTimeout(() => {
          this.playNotificationSound(false);
        }, 1200);
      }
    } catch (error) {
      console.warn('Audio notification failed:', error);
    }
  }

  /**
   * Try multiple vibration methods for better iOS support
   */
  attemptVibration(pattern) {
    try {
      // Method 1: Standard vibration API
      if ('vibrate' in navigator) {
        navigator.vibrate(pattern);
      }
      
      // Method 2: iOS specific - try to trigger through touch events
      if (this.isIOS) {
        // Create a brief touch simulation to potentially trigger haptic feedback
        const button = document.createElement('button');
        button.style.position = 'absolute';
        button.style.left = '-9999px';
        button.style.opacity = '0';
        document.body.appendChild(button);
        
        button.focus();
        button.click();
        
        setTimeout(() => {
          document.body.removeChild(button);
        }, 100);
      }
    } catch (error) {
      console.warn('Vibration failed:', error);
    }
  }

  /**
   * Visual flash notification for iOS
   */
  flashScreen(isEmergency = false) {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: ${isEmergency ? '#ff4444' : '#4ecdc4'};
      opacity: 0.8;
      z-index: 999999;
      pointer-events: none;
      animation: flashNotification 0.3s ease-out;
    `;
    
    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes flashNotification {
        0% { opacity: 0; }
        50% { opacity: 0.8; }
        100% { opacity: 0; }
      }
    `;
    document.head.appendChild(style);
    
    document.body.appendChild(overlay);
    
    setTimeout(() => {
      document.body.removeChild(overlay);
      document.head.removeChild(style);
    }, 300);
  }

  /**
   * Show a local notification with multiple alert methods
   */
  async showLocalNotification(title, options = {}) {
    const defaultOptions = {
      body: 'New activity in your queue',
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'queue-notification',
      requireInteraction: true,
      vibrate: [200, 100, 200], // Buzz pattern
      silent: false
    };

    const notificationOptions = { ...defaultOptions, ...options };
    const isEmergency = options.data?.isEmergency || false;
    let notificationShown = false;

    // Method 1: Try service worker notification
    if (this.serviceWorkerRegistration && Notification.permission === 'granted') {
      try {
        await this.serviceWorkerRegistration.showNotification(title, notificationOptions);
        notificationShown = true;
        console.log('Service worker notification shown:', title);
      } catch (error) {
        console.warn('Service worker notification failed:', error);
      }
    }
    
    // Method 2: Try native browser notification as fallback
    if (!notificationShown && Notification.permission === 'granted') {
      try {
        const notification = new Notification(title, {
          body: notificationOptions.body,
          icon: notificationOptions.icon,
          tag: notificationOptions.tag,
          requireInteraction: notificationOptions.requireInteraction,
          silent: false // Ensure sound on iOS
        });
        
        // Auto-close after delay
        setTimeout(() => {
          notification.close();
        }, 8000);
        
        notificationShown = true;
        console.log('Native notification shown:', title);
      } catch (error) {
        console.warn('Native notification failed:', error);
      }
    }

    // Method 3: Multi-modal alerts for iOS
    if (this.isIOS || this.isSafari) {
      // Visual flash
      this.flashScreen(isEmergency);
      
      // Audio notification
      await this.playNotificationSound(isEmergency);
      
      // Multiple vibration attempts
      const vibrationPattern = isEmergency ? [300, 200, 300, 200, 300] : [200, 100, 200];
      this.attemptVibration(vibrationPattern);
      
      // Repeat alerts for emergency
      if (isEmergency) {
        setTimeout(() => {
          this.attemptVibration([400, 200, 400]);
          this.flashScreen(true);
        }, 1000);
      }
    }

    // Method 4: Standard vibration for non-iOS
    if (!this.isIOS && 'vibrate' in navigator) {
      const vibrationPattern = isEmergency ? [300, 200, 300, 200, 300] : [200, 100, 200];
      navigator.vibrate(vibrationPattern);
    }

    // Method 5: Focus/wake the browser tab
    if (document.hidden) {
      window.focus();
    }

    return notificationShown;
  }

  /**
   * Show notification for new student in queue
   */
  async notifyNewStudent(studentName, isEmergency = false) {
    const title = isEmergency ? '🚨 Emergency Patient!' : '👋 New Student in Queue';
    const body = isEmergency 
      ? `${studentName} has joined as an EMERGENCY patient`
      : `${studentName} has joined the queue`;

    const options = {
      body,
      tag: 'new-student',
      data: {
        studentName,
        isEmergency,
        timestamp: new Date().toISOString()
      },
      vibrate: isEmergency ? [300, 200, 300, 200, 300] : [200, 100, 200],
      actions: [
        {
          action: 'view-queue',
          title: 'View Queue'
        },
        {
          action: 'dismiss',
          title: 'Dismiss'
        }
      ]
    };

    return await this.showLocalNotification(title, options);
  }

  /**
   * Test notification to verify setup
   */
  async testNotification() {
    return await this.showLocalNotification('Test Notification', {
      body: 'Push notifications are working! 🎉',
      tag: 'test-notification'
    });
  }

  /**
   * Check if user has granted notification permission
   */
  hasPermission() {
    return this.getPermissionStatus() === 'granted';
  }

  /**
   * Get notification settings from localStorage
   */
  getSettings() {
    const settings = localStorage.getItem('notification-settings');
    return settings ? JSON.parse(settings) : {
      enabled: false,
      newStudents: true,
      emergencies: true,
      sound: true,
      vibrate: true
    };
  }

  /**
   * Save notification settings to localStorage
   */
  saveSettings(settings) {
    localStorage.setItem('notification-settings', JSON.stringify(settings));
  }

  /**
   * Enable notifications (request permission and subscribe)
   */
  async enable() {
    const permission = await this.requestPermission();
    if (permission === 'granted') {
      await this.subscribe();
      const settings = this.getSettings();
      settings.enabled = true;
      this.saveSettings(settings);
      return true;
    }
    return false;
  }

  /**
   * Disable notifications
   */
  disable() {
    const settings = this.getSettings();
    settings.enabled = false;
    this.saveSettings(settings);
  }
}

// Export singleton instance
export const notificationService = new NotificationService();